import { useMutation } from '@tanstack/react-query';

interface BulkActionParams {
  jobPostId: number;
  candidateIds: number[];
}

interface BulkCommentParams extends BulkActionParams {
  comment: string;
}

// 選考へ進む（書類選考開始）
export function useBulkProgress() {
  return useMutation({
    mutationFn: async ({ jobPostId, candidateIds }: BulkActionParams) => {
      const response = await fetch(
        `/agent/api/job_post_endpoints/${jobPostId}/bulk_progress.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token':
              document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
          body: JSON.stringify({
            ids: candidateIds,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to progress candidates');
      }

      return response.json();
    },
  });
}

// 辞退
export function useBulkDecline() {
  return useMutation({
    mutationFn: async ({ jobPostId, candidateIds }: BulkActionParams) => {
      const response = await fetch(`/agent/api/job_post_endpoints/${jobPostId}/bulk_decline.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          ids: candidateIds,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to decline candidates');
      }

      return response.json();
    },
  });
}

// 取り下げ
export function useBulkWithdraw() {
  return useMutation({
    mutationFn: async ({ jobPostId, candidateIds }: BulkActionParams) => {
      const response = await fetch(
        `/agent/api/job_post_endpoints/${jobPostId}/bulk_withdraw.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token':
              document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
          body: JSON.stringify({
            ids: candidateIds,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to withdraw candidates');
      }

      return response.json();
    },
  });
}

// 辞退・取り下げを解除（提案へ戻す）
export function useBulkReset() {
  return useMutation({
    mutationFn: async ({ jobPostId, candidateIds }: BulkActionParams) => {
      const response = await fetch(`/agent/api/job_post_endpoints/${jobPostId}/bulk_reset.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          ids: candidateIds,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reset candidates');
      }

      return response.json();
    },
  });
}

// 物理削除（取り下げ済みのみ）
export function useBulkDestroy() {
  return useMutation({
    mutationFn: async ({ jobPostId, candidateIds }: BulkActionParams) => {
      const response = await fetch(`/agent/api/job_post_endpoints/${jobPostId}/bulk_destroy.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          ids: candidateIds,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to destroy candidates');
      }

      return response.json();
    },
  });
}

// コメント一括登録
export function useBulkComment() {
  return useMutation({
    mutationFn: async ({ jobPostId, candidateIds, comment }: BulkCommentParams) => {
      const response = await fetch(`/agent/api/job_post_endpoints/${jobPostId}/bulk_comment.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          comment: candidateIds.map((id) => ({ id, content: comment })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update comments');
      }

      return response.json();
    },
  });
}
